import type { IconName, StatusKind } from "@sagot/ui";
import { z } from "zod";
import type { Booking, BookingStatus } from "./lead-types";

/** Status tabs of the Bookings view (`?view=bookings&status=`); "all" has no query value. */
export const BOOKING_FILTERS = ["all", "requested", "confirmed", "declined", "cancelled"] as const;
export type BookingFilter = (typeof BOOKING_FILTERS)[number];
/** Matches backend `BOOKING_DECISION_NOTE_MAX`. */
export const DECISION_NOTE_MAX = 500;
export const BOOKINGS_PAGE_SIZE = 25;

export function parseBookingFilter(value: string | null): BookingFilter {
  return BOOKING_FILTERS.find((filter) => filter === value) ?? "all";
}

export function filterStatus(filter: BookingFilter): BookingStatus | null {
  return filter === "all" ? null : filter;
}

export interface StatusLook {
  kind: StatusKind;
  icon: IconName;
}

const LOOKS: Readonly<Record<string, StatusLook>> = {
  requested: { kind: "warning", icon: "clock" },
  confirmed: { kind: "success", icon: "check-circle" },
  declined: { kind: "danger", icon: "x-circle" },
  cancelled: { kind: "neutral", icon: "x" },
  pending: { kind: "neutral", icon: "clock" },
  failed: { kind: "danger", icon: "alert-triangle" },
};

/** Status = icon + words + colour; unknown statuses stay neutral. */
export function statusLook(status: string): StatusLook {
  return LOOKS[status] ?? { kind: "neutral", icon: "circle" };
}

/** Staff can answer a request only while it is still waiting (`requested`). */
export function canDecide(booking: Pick<Booking, "status">, canEdit: boolean): boolean {
  return canEdit && booking.status === "requested";
}

/** Staff can call off a booking that is confirmed (a request is declined instead), and only with edit rights. */
export function canCancel(booking: Pick<Booking, "status">, canEdit: boolean): boolean {
  return canEdit && booking.status === "confirmed";
}

export interface SlotLabel {
  date: string;
  time: string;
}

/** The slot in one time zone: "Mon, Oct 19" and "9:00 AM – 9:30 AM". */
export function slotLabel(startIso: string, endIso: string, timeZone: string, locale: string): SlotLabel {
  const date = new Intl.DateTimeFormat(locale, { timeZone, weekday: "short", month: "short", day: "numeric" });
  const time = new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric", minute: "2-digit" });
  const start = new Date(startIso);
  return { date: date.format(start), time: `${time.format(start)} – ${time.format(new Date(endIso))}` };
}

/** The visitor's own wall-clock time, or `null` when it reads the same as the workspace time. */
export function visitorTimeLabel(booking: Pick<Booking, "slot_start" | "slot_end" | "timezone">, workspaceZone: string, locale: string): { time: string; zone: string } | null {
  const own = slotLabel(booking.slot_start, booking.slot_end, workspaceZone, locale);
  const theirs = slotLabel(booking.slot_start, booking.slot_end, booking.timezone, locale);
  if (own.date === theirs.date && own.time === theirs.time) return null;
  return { time: `${theirs.date}, ${theirs.time}`, zone: booking.timezone };
}

/** Only https links from the server are rendered as links (calendar event, Meet). */
export function safeHttpsUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value).protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

export function decisionSchema(tooLong: string) {
  return z.object({ note: z.string().trim().max(DECISION_NOTE_MAX, tooLong) });
}
export type DecisionValues = z.infer<ReturnType<typeof decisionSchema>>;

/** `POST …/confirm|decline|cancel` body: a blank note is sent as `null`. */
export function decisionBody(values: DecisionValues): { note: string | null } {
  return { note: values.note.trim() || null };
}
